import React from "react"
import {
  HStack, VStack, Input,
} from "@chakra-ui/react"
import { HexColorPicker } from "react-colorful"
import {
  Control, FieldValues, Path, PathValue, useController,
} from "react-hook-form"
import ColorCircle from "components/ReactHookForm/ColorSelect/ColorCircle"
import { colorIsValid, colorWithHash } from "utilities/colors"
import { useIsMixed } from "./MixedValues"
import useHexDraft from "./useHexDraft"

type Props<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  defaultValue?: string
  /** The colors offered as swatches (the first 12) */
  swatches: readonly string[]
  /** A color wheel and a hex field besides the swatches */
  allowCustom?: boolean
}

/**
 * The inside of a color popover: swatches, and a color wheel with a hex field when custom colors
 * are allowed. It always shows the field's color.
 */
const ColorPicker = <T extends FieldValues, >({
  name,
  control,
  defaultValue = "#000000",
  swatches,
  allowCustom = true,
}: Props<T>) => {
  const { field } = useController({
    name,
    control,
    defaultValue: defaultValue as PathValue<T, Path<T>>,
  })
  const mixed = useIsMixed(name)
  const color = (field.value as string | null | undefined) || defaultValue
  const hex = color === "transparent" ? "#000000" : color
  const hexDraft = useHexDraft(hex)

  const handleColorChange = (newColor: string) => {
    if (colorIsValid(newColor)) field.onChange(colorWithHash(newColor))
  }

  const handleTextInputChange = (input: string) => {
    const typed = hexDraft.type(input)
    if (typed) field.onChange(typed)
  }

  return (
    <VStack spacing={3} align="stretch">
      <HStack wrap="wrap" spacing={2}>
        {swatches.slice(0, 12).map((swatch) => (
          <ColorCircle
            key={swatch}
            color={swatch}
            selected={!mixed && color === swatch}
            onSelect={handleColorChange}
          />
        ))}
      </HStack>

      {allowCustom && (
        <VStack spacing={3}>
          <HexColorPicker color={hex} onChange={handleColorChange} />
          <Input
            value={hexDraft.text}
            onChange={(e) => handleTextInputChange(e.target.value)}
            placeholder="#000000"
            size="sm"
            bg="white"
          />
        </VStack>
      )}
    </VStack>
  )
}

export default ColorPicker
